import { Component, OnInit, Input, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '../../../../environments/environment';

import { ActionCableService } from '../../../services/common/action-cable.service';
import { AlertService } from '../../../services/common/alert.service';
import { AuthService } from '../../../services/common/auth.service';
import { ConflictService } from '../../../services/conflicts/conflict.service';
import { DocumentStoreService } from '../../../services/documents/document-store.service';
import { InvolvementService } from '../../../services/matters/involvement.service';
import { MatterService } from '../../../services/matters/matter.service';
import { MatterStoreService } from '../../../services/matters/matter-store.service';
import { NotyService } from '../../../services/common/noty.service';

@Component({
  selector: 'app-matter-administration',
  templateUrl: './matter-administration.component.html',
  styleUrls: ['./matter-administration.component.css'],
})
export class MatterAdministrationComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() matter_id: number;

  archiveStatus: string | null;

  archiving: boolean = false;

  channelSubscription: any;

  cloning: boolean = false;

  intranet: any;

  intranetEditable: boolean = true;

  isAdmin: boolean = false;

  isTech: boolean = false;

  matterClone: any = {
    is_private: '0',
    is_personnel: '0',
    in_collections: '0',
    involvements: '0',
    matter_users: '0',
    matter_dates: '0',
    details: '0',
    documents: '0',
    matter_histories: '0',
    tags: '0',
    matter_type_id: 5,
    rate_type_id: 5,
  };

  matterTypes: Array<any> = [];

  optionsExpand: any = {};

  rateTypes: Array<any> = [];

  userGroups: Array<any> = [];

  matterArchiveOptions: any = {
    archiveModels: [
      { label: 'documents', value: 'documents' },
      { label: 'histories', value: 'histories' },
    ],
    after: [
      { label: '1 month ago', value: '1 month ago' },
      { label: '2 months ago', value: '2 months ago' },
      { label: '6 months ago', value: '6 months ago' },
      { label: '1 year ago', value: '1 year ago' },
      { label: '18 months ago', value: '18 months ago' },
      { label: '2 years ago', value: '2 years ago' },
      { label: '3 years ago', value: '3 years ago' },
    ],
  };

  matterArchiveParams: any = {
    types: [],
    after: '6 months ago',
    commit: 'Go',
  };

  production: boolean = false;

  reindexingDocumentsPlus: boolean = false;

  reindexingSolr: boolean = false;

  documentsReindexStatus: string = '';

  isArchived: boolean = false;

  // Conflict check properties
  checkingConflicts: boolean = false;
  conflictResults: any = null;
  roleExpanded: any = {};
  conflictsByRole: any[] = [];
  approvingConflicts: boolean = false;
  canApproveConflicts: boolean = false;

  constructor(
    private actionCableService: ActionCableService,
    private alertService: AlertService,
    private authService: AuthService,
    public changeDetectorRef: ChangeDetectorRef,
    private conflictService: ConflictService,
    private documentStoreService: DocumentStoreService,
    private involvementService: InvolvementService,
    private matterService: MatterService,
    public matterStoreService: MatterStoreService,
    private notyService: NotyService,
    private router: Router
  ) {
    this.production = environment.production;
    this.matterStoreService.matterAdministrationComponent = this;
    // Safely access is_archived - it may not be loaded yet
    const matterInfo = this.matterStoreService.matterShowComponent?.matter_info;
    this.isArchived = matterInfo?.is_archived || false;
    this.matterService.getMatterCloneOptions().then((response_json) => {
      this.matterTypes = response_json.matter_types;
      this.rateTypes = response_json.rate_types;
    });
  }

  // Component lifecycle events
  ngOnInit() {
    console.log('initialized matter-administration component');
    this.authService.getCurrentUserGroups().then((response_json) => {
      this.isTech = this.authService.getUser().is('tech');
      this.isAdmin = this.authService.getUser().is('admin');
      this.checkApprovalPermission();
    });
    this.subscribeArchiveChannel();
    this.loadExistingConflictStatus();
  }

  // Load existing conflict check status for this matter
  loadExistingConflictStatus() {
    if (!this.matter_id) return;

    this.conflictService
      .getMatterStatus(this.matter_id)
      .then((response: any) => {
        if (response.latest_check && response.latest_check.needs_approval) {
          // There's an existing unapproved conflict check - display it
          this.conflictResults = {
            success: true,
            conflict_check_id: response.latest_check.id,
            conflicts: response.latest_check.conflicts || [],
            searched_contacts: response.latest_check.searched_contacts || [],
            approved: response.latest_check.approved,
            approved_by_name: response.latest_check.approved_by_name,
            approved_at: response.latest_check.approved_at,
            needs_approval: response.latest_check.needs_approval
          };
          this.conflictsByRole = this.buildConflictsByRole();
        }
      })
      .catch((error: any) => {
        console.error('Error loading conflict status:', error);
      });
  }

  checkApprovalPermission() {
    // Can approve if admin or lead attorney
    if (this.isAdmin) {
      this.canApproveConflicts = true;
      return;
    }
    // Check if current user is lead attorney
    const matterInfo = this.matterStoreService.matterShowComponent?.matter_info;
    const leadAttorneyId = matterInfo?.lead_attorney?.id;
    const currentUserId = this.authService.getUser()?.id;
    this.canApproveConflicts = leadAttorneyId && currentUserId && leadAttorneyId === currentUserId;
  }

  ngAfterViewInit() {
    this.authService.fetchUserGroups().then((response_json) => (this.userGroups = response_json.groups));
  }

  ngOnDestroy() {
    this.clearChannelSubscription();
    this.matterStoreService.matterAdministrationComponent = null;
  }

  // Component methods
  archive(partial: boolean = false, restore: boolean = false) {
    this.archiving = true;
    let params: any = {};
    if (partial) params = this.matterArchiveParams;
    else if (restore) params = { after: this.matterArchiveParams.after, commit: 'Restore', types: this.matterArchiveParams.types };
    this.matterService
      .archiveMatter(this.matter_id, params)
      .then((response_json) => {
        this.archiving = false;
        if (response_json.success) {
          this.alertService.alert_status_success(response_json.text || 'Success archiving');
          // Reload page after successful archive/restore to reflect changes
          setTimeout(() => window.location.reload(), 1500);
        } else {
          let errorMsg = response_json.text || 'Error archiving';
          if (response_json.action_required) errorMsg += '\n' + response_json.action_required;
          this.archiveStatus = errorMsg;
          this.alertService.alert_status_danger_overlay(errorMsg, 'Archive Failed');
        }
        console.log('Matter archive response', response_json);
      })
      .catch(() => {
        this.archiving = false;
        this.archiveStatus = 'Error archiving - request failed';
        this.alertService.alert_status_danger_overlay('An unexpected error occurred while archiving.', 'Archive Failed');
      });
  }

  clearChannelSubscription() {
    this.archiveStatus = '';
    if(this.channelSubscription){
      this.channelSubscription.unsubscribe();
    }
  }

  clearParentMatter() {
    this.matterClone.parent_matter_id = undefined;
    this.intranetEditable = false;
    this.loadMatterTypes();
  }

  clone() {
    this.cloning = true;
    this.matterService
      .clone(this.matterClone, this.matter_id)
      .then((response_json) => {
        this.cloning = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Cloning completed', 'success');
          if (response_json.clone_matter_id) {
            this.router.navigateByUrl(`/matters/${response_json.clone_matter_id}`);
          } else if (this.matterStoreService.matterHistoriesComponent) {
            this.matterStoreService.matterHistoriesComponent.loadHistories();
          }
        } else this.notyService.notify(response_json.text || 'Error in cloning', 'error');
      })
      .catch(() => {
        this.cloning = false;
        this.notyService.notify('Error in cloning', 'error');
      });
  }

  onIntranetSelect(intranet: any) {
    this.intranet = intranet;
    this.matterClone.intranet_id = intranet.id;
    this.loadMatterTypes();
  }

  onParentMatterSelect(parent_matter_id: any) {
    this.matterClone.parent_matter_id = Number(parent_matter_id);
    this.matterClone.intranet_id = undefined;
    this.intranet = undefined;
    this.intranetEditable = false;
    this.loadMatterTypes();
  }

  loadMatterTypes() {
    this.matterService.getMatterTypes(this.matterClone.intranet_id).then((response_json) => (this.matterTypes = response_json));
  }

  reindexSolr = (type: string) => {
    console.log('called reindexSolr');
    let types: Array<string>;
    switch (type) {
      case 'documents':
        types = ['documents'];
        break;
      case 'involvements':
        types = ['involvements'];
        break;
      case 'histories':
        types = ['histories'];
        break;
      default:
        types = ['all'];
    }
    this.reindexingSolr = true;
    this.matterService
      .reindexSolr(this.matter_id, types)
      .then((response_json) => {
        console.log('reindexSolr response', response_json);
        this.reindexingSolr = false;
        if (response_json.success) {
          this.alertService.alert_status_success(response_json.text || 'Reindex successful');
          this.refreshLoadedReindexSections(type);
        } else this.alertService.alert_status_danger(response_json.text || 'Error reindexing solr');
      }, (error) => {
        console.log('reindexSolr error', error);
        this.reindexingSolr = false;
        this.alertService.alert_status_danger('Error reindexing solr');
      });
  };

  private refreshLoadedReindexSections(type: string) {
    const refreshes: Array<{ name: string; refresh: (() => void) | undefined }> = [];
    const refreshAll = !['documents', 'involvements', 'histories'].includes(type);

    if (type === 'documents' || refreshAll) {
      const documentsComponent = this.documentStoreService.matterDocumentsListComponent;
      refreshes.push({ name: 'documents', refresh: documentsComponent ? () => documentsComponent.loadDocuments() : undefined });
    }
    if (type === 'involvements' || refreshAll) {
      const involvementsComponent = this.involvementService.involvementsComponent;
      refreshes.push({ name: 'involvements', refresh: involvementsComponent ? () => involvementsComponent.loadInvolvements() : undefined });
    }
    if (type === 'histories' || refreshAll) {
      const historiesComponent = this.matterStoreService.matterHistoriesComponent;
      refreshes.push({ name: 'histories', refresh: historiesComponent ? () => historiesComponent.loadHistories() : undefined });
    }

    refreshes.forEach((section) => {
      if (!section.refresh) return;

      try {
        section.refresh();
      } catch (error) {
        // The Solr request has already succeeded. A tab refresh failure must not
        // replace that result with the misleading "Error reindexing Solr" alert.
        console.warn(`Solr reindex succeeded, but the loaded ${section.name} tab could not be refreshed`, error);
      }
    });
  }

  reindexDocumentsPlus = () => {
    console.log('called reindexDocumentsPlus');
    this.reindexingDocumentsPlus = true;
    this.documentsReindexStatus = 'Starting document reindex from files...';
    this.matterService
      .reindexDocumentsPlus(this.matter_id)
      .then((response_json) => {
        console.log('reindexDocumentsPlus response', response_json);
        this.reindexingDocumentsPlus = false;
        if (response_json.success) {
          this.alertService.alert_status_success(response_json.text || 'Documents+ reindex successful');
          // Show summary and error details if any
          let status = response_json.summary || '';
          if (response_json.error_details) {
            status += '\nErrors: ' + response_json.error_details;
          }
          this.documentsReindexStatus = status;
          this.documentStoreService.matterDocumentsListComponent!.loadDocuments();
        } else {
          this.alertService.alert_status_danger(response_json.text || 'Error in Documents+ reindex');
          this.documentsReindexStatus = response_json.text || '';
        }
      })
      .catch((error) => {
        console.log('reindexDocumentsPlus error', error);
        this.reindexingDocumentsPlus = false;
        this.documentsReindexStatus = '';
        this.alertService.alert_status_danger('Error in Documents+ reindex');
      });
  };

  restoreArchived() {
    this.archive(false, true);
  }

  subscribeArchiveChannel() {
    this.actionCableService.cable().then((cable) => {
      this.channelSubscription = cable.subscriptions.create(
        {
          channel: 'MatterChannel',
          matter_id: this.matter_id,
          archive: true,
        },
        {
          received: (data: any) => {
            if (data.content) this.archiveStatus = data.content;
          },
          disconnected: () => {
            this.archiveStatus = '';
          },
        }
      );
    });
  }

  typeChange(checked: boolean, type: string) {
    let index: number = this.matterArchiveParams.types.indexOf(type);
    if (checked) {
      if (index < 0) this.matterArchiveParams.types.push(type);
    } else {
      if (index > -1) this.matterArchiveParams.types.splice(index, 1);
    }
  }

  // Conflict check methods
  checkConflicts() {
    this.checkingConflicts = true;
    this.conflictResults = null;
    this.conflictsByRole = [];
    this.roleExpanded = {};
    this.conflictService
      .checkMatter(this.matter_id)
      .then((response: any) => {
        this.checkingConflicts = false;
        if (response.success) {
          this.conflictResults = response;
          this.conflictsByRole = this.buildConflictsByRole();
          if (response.conflicts && response.conflicts.length > 0) {
            this.alertService.alert_status_warning(`Found ${response.conflicts.length} potential conflict(s)`);
            // Refresh matter info to show frozen banner
            if (this.matterStoreService.matterShowComponent) {
              this.matterStoreService.matterShowComponent.loadMatterInfo();
            }
          } else {
            this.alertService.alert_status_success('No conflicts found');
          }
        } else {
          this.alertService.alert_status_danger(response.text || 'Error checking conflicts');
        }
      })
      .catch((error: any) => {
        this.checkingConflicts = false;
        this.alertService.alert_status_danger('Error checking conflicts');
        console.error('Conflict check error:', error);
      });
  }

  // Group conflicts by role type, with matters sorted by number descending
  buildConflictsByRole(): any[] {
    if (!this.conflictResults?.conflicts) return [];

    const byRole: { [key: string]: any } = {};

    this.conflictResults.conflicts.forEach((conflict: any) => {
      // A conflict can have multiple roles
      const roles = conflict.roles && conflict.roles.length > 0 ? conflict.roles : ['Unknown'];

      roles.forEach((role: string) => {
        if (!byRole[role]) {
          byRole[role] = {
            role: role,
            conflicts: []
          };
        }
        byRole[role].conflicts.push(conflict);
      });
    });

    // Convert to array, sort conflicts by matter number descending, sort role groups by count
    return Object.values(byRole)
      .map((roleGroup: any) => {
        // Sort conflicts by matter_number descending (as numbers)
        roleGroup.conflicts.sort((a: any, b: any) => {
          const numA = parseInt(a.matter_number) || 0;
          const numB = parseInt(b.matter_number) || 0;
          return numB - numA;
        });
        return roleGroup;
      })
      .sort((a: any, b: any) => b.conflicts.length - a.conflicts.length);
  }

  toggleRoleExpand(role: string) {
    this.roleExpanded[role] = !this.roleExpanded[role];
  }

  approveConflicts() {
    if (!this.conflictResults?.conflict_check_id) {
      this.alertService.alert_status_danger('No conflict check to approve');
      return;
    }

    this.approvingConflicts = true;
    this.conflictService
      .approve(this.conflictResults.conflict_check_id)
      .then((response: any) => {
        this.approvingConflicts = false;
        if (response.success) {
          this.alertService.alert_status_success('Conflicts approved');
          // Update local state
          this.conflictResults.approved = true;
          this.conflictResults.approved_by_name = response.approved_by;
          this.conflictResults.approved_at = response.approved_at;
          this.conflictResults.needs_approval = false;
          // Notify matter store to update frozen status
          if (this.matterStoreService.matterShowComponent) {
            this.matterStoreService.matterShowComponent.loadMatterInfo();
          }
        } else {
          this.alertService.alert_status_danger(response.error || 'Error approving conflicts');
        }
      })
      .catch((error: any) => {
        this.approvingConflicts = false;
        this.alertService.alert_status_danger('Error approving conflicts');
        console.error('Conflict approval error:', error);
      });
  }
}
